import { useContext } from "react";
import { useTranslation } from "react-i18next";

import { Column } from "primereact/column";
import { DataTable } from "primereact/datatable";

import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorIcon from "@mui/icons-material/Error";
import { Box, CircularProgress, Tooltip, Typography, useTheme } from "@mui/material";

import { faCircleNotch } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import { NewWorkorderWithMetadata } from "../../shared/interfaces/WorkOrder";
import { BulkWorkorderContext } from "./BulkWorkorderContext";

export function BulkWorkorderSummary() {
  const { t } = useTranslation();
  const { workorderList } = useContext(BulkWorkorderContext);
  const theme = useTheme();

  const creationStatusCellTemplate = (rowData: NewWorkorderWithMetadata) => {
    switch (rowData.creationStatus) {
      case "loading":
        return (
          <Tooltip title={t("workorders.creatingWorkorder")}>
            <CircularProgress size="1.5em" />
          </Tooltip>
        );

      case "error":
        return (
          <Tooltip title={t("workorders.creatingWorkorderError")} sx={{ zIndex: 3500 }}>
            <ErrorIcon color="error" />
          </Tooltip>
        );

      case "success":
        return (
          <Tooltip
            title={t("common.dataSuccessfullyCreated", {
              entity: t("workorders.workorder"),
              id: rowData.createdWorkorderId,
            })}
          >
            <Box>
              <CheckCircleIcon color="success" />
              <Typography variant="caption" component="p">
                {rowData.createdWorkorderId} {t("common.created")}
              </Typography>
            </Box>
          </Tooltip>
        );

      default:
        return (
          <Tooltip title={t("workorders.workorderCreationPending")}>
            <Box>
              <FontAwesomeIcon icon={faCircleNotch} fixedWidth size="lg" color={theme.palette.text.disabled} />
            </Box>
          </Tooltip>
        );
    }
  };

  const dateCellTemplate = (date: Date | undefined) =>
    date ? t("common.formattedDateShort", { dateString: date }) : undefined;

  return (
    <>
      <DataTable value={workorderList}>
        <Column field="newWorkorder.description" header={t("common.summary")} />
        <Column field="newWorkorder.longDescription" header={t("common.detailedDescription")} />
        <Column
          field="newWorkorder.targetStartDate"
          header={t("workorders.targetStartDate")}
          body={(rowData: NewWorkorderWithMetadata) => dateCellTemplate(rowData.newWorkorder.targetStartDate)}
        />
        <Column
          field="newWorkorder.targetCompletionDate"
          header={t("workorders.targetCompletionDate")}
          body={(rowData: NewWorkorderWithMetadata) => dateCellTemplate(rowData.newWorkorder.targetCompletionDate)}
        />
        <Column field="newWorkorder.substationName" header={t("substations.substation")} />
        <Column
          field="switchgearNames"
          header={t("asset.assets")}
          body={(rowData: NewWorkorderWithMetadata) => rowData.assetNames?.join(", ") || ""}
        />
        <Column field="workflowName" header={t("workflows.workflow")} />
        <Column header={t("workorders.creationStatus")} align="center" body={creationStatusCellTemplate} />
      </DataTable>
    </>
  );
}
